// Helper murni untuk DUPLIKAT PRODUK — memutus keterkaitan file media salinan
// dengan produk sumbernya.
//
// Bug: saat menduplikat produk (`/admin/products/new?duplicate=<id>`), foto
// produk & gambar per-opsi disalin sebagai URL apa adanya. URL itu menunjuk ke
// folder storage produk LAMA (`products/<sourceId>/…`), jadi salinan "berbagi"
// file fisik dengan aslinya. Untuk produk arsip (CLOSED PO) ini berbahaya: purge
// foto produk arsip (mig 201/202) menghapus SELURUH folder `products/<sourceId>/`
// → foto salinan ikut hilang. Salinan harus punya file di foldernya sendiri.
//
// Fungsi di sini menghitung path sumber & tujuan penyalinan storage (murni,
// mudah diuji), dan `duplicateProductMediaUrl` menjalankannya lewat operasi
// Storage yang DISUNTIKKAN pemanggil (ProductForm) — best-effort: gagal salin →
// pakai URL sumber. Foto WebP disalin sebagai JPEG (lihat blok DUPLIKAT ×
// KATALOG INSTAGRAM di bawah).

export const PRODUCT_MEDIA_BUCKET = "product-media";

// Dari URL publik Supabase Storage → path relatif terhadap bucket product-media,
// atau null bila URL bukan objek bucket tersebut. Toleran querystring & encoding.
export function productMediaStoragePath(url: string | null | undefined): string | null {
  if (!url) return null;
  const marker = `/${PRODUCT_MEDIA_BUCKET}/`;
  const i = url.indexOf(marker);
  if (i === -1) return null;
  const raw = url.slice(i + marker.length).split("?")[0].split("#")[0];
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

// Rencana penyalinan satu file media ke folder produk BARU. Mengembalikan null
// (tak perlu / tak bisa disalin) bila:
//   • URL bukan objek bucket product-media,
//   • path tidak berbentuk `[<store_id>/]products/<ownerId>/<rest>`,
//   • file sudah milik produk tujuan (ownerId === newProductId), atau
//   • newProductId kosong.
// Sub-path dipertahankan (mis. `options/<file>` → tetap di bawah folder produk
// baru) agar gambar per-opsi tetap rapi. Prefix store (multi-tenant Fase 4,
// mig 3120 — path toko non-legacy diawali `<store_id>/`) ikut dipertahankan
// karena duplikat selalu terjadi di dalam toko yang sama.
export function planDuplicatedMediaCopy(
  url: string | null | undefined,
  newProductId: string
): { srcPath: string; destPath: string } | null {
  const srcPath = productMediaStoragePath(url);
  if (!srcPath) return null;
  const m = /^((?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/)?)products\/([^/]+)\/(.+)$/.exec(
    srcPath
  );
  if (!m) return null;
  const storePrefix = m[1];
  const ownerId = m[2];
  const rest = m[3];
  if (!newProductId || ownerId === newProductId) return null;
  return { srcPath, destPath: `${storePrefix}products/${newProductId}/${rest}` };
}

// ── DUPLIKAT × KATALOG INSTAGRAM: salinan WebP dibuat ULANG sebagai JPEG ────
//
// Katalog Meta hanya menerima JPEG/PNG (`isMetaImageUrl`, metaCatalog.ts). Sejak
// PR-IG-FOTO unggahan BARU sudah JPEG, tetapi Duplikat menyalin berkas SUMBER
// apa adanya: produk lama ber-foto WebP melahirkan salinan WebP yang dilewati
// feed ("Foto masih WebP") walau produknya baru dibuat (pemilik 2026-09-30:
// keenam PO berharga galea hasil duplikat, semuanya WebP). Karena itu foto
// WebP warisan tidak di-`copy`, melainkan diunduh → dikonversi di BROWSER
// dengan pipa unggahan yang SAMA (`compressImageFile` + preset `productPhoto`,
// latar putih) → diunggah sebagai `.jpg` ke folder produk baru. Nama mengikuti
// skrip konversi foto lama (`jpegCandidatePath`, scripts/lib/konversi-foto-jpeg.mjs):
// `<nama>.webp` → `<nama>.jpg`. Berkas SUMBER tak disentuh.
//
// FAIL-OPEN, persis perilaku lama: konversi yang tak menghasilkan JPEG (WebP
// animasi, decode gagal) atau unduh/unggah yang gagal → jatuh ke `copy` apa
// adanya; `copy` gagal → URL sumber. Duplikat tak pernah gagal karena foto.

const WEBP_PATH_RE = /\.webp$/i;

/**
 * Path tujuan `.jpg` untuk salinan duplikat yang WAJIB dibuat ulang sebagai
 * JPEG, atau null bila sumbernya bukan WebP (JPEG/PNG diterima Meta → cukup
 * disalin apa adanya, tanpa kehilangan mutu).
 */
export function duplicatedMediaJpegPath(plan: { srcPath: string; destPath: string }): string | null {
  if (!WEBP_PATH_RE.test(plan.srcPath)) return null;
  return plan.destPath.replace(WEBP_PATH_RE, ".jpg");
}

/**
 * True bila foto/gambar-opsi duplikat ini akan DIKONVERSI (bukan sekadar
 * disalin) — dipakai ProductForm menghitung "Foto N dari M" dengan jujur:
 * konversi = unduh + olah di browser + unggah, jadi ia berkas yang dikerjakan.
 */
export function duplicateNeedsJpeg(url: string | null | undefined, newProductId: string): boolean {
  const plan = planDuplicatedMediaCopy(url, newProductId);
  return plan !== null && duplicatedMediaJpegPath(plan) !== null;
}

/** Operasi Storage yang dipakai `duplicateProductMediaUrl` — disuntikkan pemanggil. */
export type DuplicateMediaStorage = {
  copy(srcPath: string, destPath: string): Promise<{ error: unknown }>;
  download(path: string): Promise<{ data: Blob | null; error: unknown }>;
  /** Unggah JPEG hasil konversi (pemanggil memasang `upsert: false` + cacheControl). */
  upload(path: string, file: File): Promise<{ error: unknown }>;
  publicUrl(path: string): string;
};

/**
 * Satu foto/gambar-opsi DUPLIKAT → URL finalnya di folder produk BARU.
 * `toJpeg` = konverter unggahan foto produk (browser); hasil selain
 * `image/jpeg` dianggap gagal dan jatuh ke salinan apa adanya.
 */
export async function duplicateProductMediaUrl(input: {
  url: string;
  newProductId: string;
  storage: DuplicateMediaStorage;
  toJpeg: (file: File) => Promise<File>;
}): Promise<string> {
  const { url, newProductId, storage, toJpeg } = input;
  const plan = planDuplicatedMediaCopy(url, newProductId);
  if (!plan) return url;

  const jpegPath = duplicatedMediaJpegPath(plan);
  if (jpegPath) {
    try {
      const { data, error } = await storage.download(plan.srcPath);
      if (!error && data) {
        const name = plan.srcPath.slice(plan.srcPath.lastIndexOf("/") + 1);
        // Tipe dipaksa WebP: path-nya memang `.webp`, sedangkan tipe Blob hasil
        // unduhan bisa kosong/octet-stream dan membuat konverter menolak.
        const jpeg = await toJpeg(new File([data], name, { type: "image/webp" }));
        if (jpeg.type === "image/jpeg") {
          const { error: upErr } = await storage.upload(jpegPath, jpeg);
          if (!upErr) {
            const publicUrl = storage.publicUrl(jpegPath);
            if (publicUrl) return publicUrl;
          }
        }
      }
    } catch {
      // jatuh ke salinan apa adanya di bawah
    }
  }

  try {
    const { error } = await storage.copy(plan.srcPath, plan.destPath);
    if (error) return url;
    return storage.publicUrl(plan.destPath) || url;
  } catch {
    return url;
  }
}

// ── CLONE LINTAS-TENANT (mig 5850) ──────────────────────────────────────────
//
// `storage_path` apa adanya (bukan URL): terima hanya bila bentuknya memang
// path di dalam bucket, supaya string sembarang tidak lolos jadi "path".
function plainBucketPath(raw: string | null | undefined): string | null {
  const value = (raw ?? "").trim();
  if (!value || /^https?:\/\//i.test(value) || value.startsWith("/")) return null;
  return /^(?:[0-9a-fA-F-]{36}\/)?products\//.test(value) ? value : null;
}

// Saudara `planDuplicatedMediaCopy` di atas, dan bedanya justru yang penting:
// di sana prefix toko DIPERTAHANKAN karena duplikat selalu terjadi di dalam
// satu toko. Di sini berkasnya menyeberang tenant, jadi prefix-nya WAJIB
// diganti ke toko tujuan — kalau tidak, salinan Tenant 2 tetap tinggal di
// folder Tenant 1 dan ikut terhapus saat Tenant 1 mem-purge produk arsipnya
// (mig 2010/3120/3380), persis bug yang melahirkan berkas ini.
//
// Toko yang memegang storage legacy (`stores.owns_legacy_storage`, mig 3120)
// memakai path TANPA prefix toko; kalau toko TUJUAN adalah toko itu, prefix
// dibuang seluruhnya alih-alih diganti.
export function planClonedMediaCopy(
  urlOrPath: string | null | undefined,
  destStoreId: string,
  destProductId: string,
  destOwnsLegacyStorage: boolean
): { srcPath: string; destPath: string } | null {
  // Menerima DUA bentuk karena pemanggilnya memang punya dua: `product_media`
  // menyimpan `storage_path` (sudah relatif terhadap bucket) sedangkan
  // `variant_options` hanya punya `image_url`. Menuntut satu bentuk saja
  // memaksa pemanggil menebak, dan tebakan yang salah = gambar hilang diam-diam.
  const srcPath = productMediaStoragePath(urlOrPath) ?? plainBucketPath(urlOrPath);
  if (!srcPath) return null;
  if (!destStoreId || !destProductId) return null;
  // Sama seperti saudaranya: `[<store_id>/]products/<ownerId>/<rest>`. Yang
  // tidak berbentuk itu bukan berkas produk dan tidak disalin.
  const m = /^((?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/)?)products\/([^/]+)\/(.+)$/.exec(
    srcPath
  );
  if (!m) return null;
  const rest = m[3];
  const destPrefix = destOwnsLegacyStorage ? "" : `${destStoreId}/`;
  const destPath = `${destPrefix}products/${destProductId}/${rest}`;
  // Sumber & tujuan identik berarti tak ada yang perlu disalin — dan menyalin
  // berkas ke atas dirinya sendiri adalah error di Storage.
  if (destPath === srcPath) return null;
  return { srcPath, destPath };
}
